/**
 * HelpDrawerHeader — bandeau d'en-tête du Help Drawer (extrait de HelpDrawer pour
 * tenir sous la limite de 300 lignes). Partagé entre les modes `docked` et
 * `overlay`. Le violet IA passe par le token `--ai`.
 */

import { useTranslation } from 'react-i18next'
import { ArrowLeft, Layers, Lightbulb, PanelRight, Pin, RotateCcw, X } from 'lucide-react'

import { type HelpDrawerMode } from '../../hooks/useHelp'
import { SheetHeader } from '../ui/sheet'
import { Button } from '../ui/button'

export interface HelpDrawerHeaderProps {
    title: string
    description?: string | null
    back: { onClick: () => void; label: string } | null
    showBeta: boolean
    showPinned: boolean
    /** Chip « Nouvelle conversation » (fil IA actif, hors plafond de tours). */
    showNewConversation: boolean
    onNewConversation: () => void
    /** Mode courant + bascule overlay ↔ ancré (desktop only). */
    mode: HelpDrawerMode
    onToggleMode?: () => void
    onClose: () => void
}

export function HelpDrawerHeader({
    title,
    description,
    back,
    showBeta,
    showPinned,
    showNewConversation,
    onNewConversation,
    mode,
    onToggleMode,
    onClose,
}: HelpDrawerHeaderProps) {
    const { t } = useTranslation()
    return (
        <div className="flex items-center justify-between border-b border-border bg-muted/30 px-4 py-3 pt-[calc(0.75rem+env(safe-area-inset-top))]">
            <SheetHeader className="min-w-0 flex-1 space-y-0">
                <div className="flex min-w-0 items-center gap-3">
                    {back ? (
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={back.onClick}
                            className="h-9 w-9 shrink-0 rounded-xl bg-card shadow-sm hover:bg-muted"
                            aria-label={back.label}
                        >
                            <ArrowLeft className="h-4 w-4 text-foreground" />
                        </Button>
                    ) : (
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent">
                            <Lightbulb className="h-4 w-4 text-primary" />
                        </div>
                    )}
                    <div className="min-w-0 flex-1">
                        <div className="flex min-w-0 items-center gap-2">
                            <span className="truncate text-lg font-bold text-foreground">{title}</span>
                            {showBeta && (
                                <span className="shrink-0 rounded-full bg-ai/15 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-ai">
                                    {t('help.assistant.beta')}
                                </span>
                            )}
                            {showPinned && (
                                <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                                    <Pin className="h-3 w-3" />
                                    {t('help.pinnedWhileReading')}
                                </span>
                            )}
                        </div>
                        {description && (
                            <p className="mt-0.5 text-xs text-muted-foreground">{description}</p>
                        )}
                    </div>
                </div>
            </SheetHeader>
            <div className="flex items-center gap-1">
                {showNewConversation && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={onNewConversation}
                        aria-label={t('help.assistant.newConversation')}
                        className="h-8 gap-1.5 rounded-full px-2.5 text-xs font-medium text-ai hover:bg-ai/15 hover:text-ai"
                    >
                        <RotateCcw className="h-3.5 w-3.5" />
                        <span className="hidden sm:inline">{t('help.assistant.newConversation')}</span>
                    </Button>
                )}
                {onToggleMode && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={onToggleMode}
                        className="hidden h-9 w-9 text-muted-foreground lg:inline-flex"
                        aria-label={mode === 'docked' ? t('help.overlayMode') : t('help.dockMode')}
                    >
                        {mode === 'docked' ? (
                            <Layers className="h-4 w-4" />
                        ) : (
                            <PanelRight className="h-4 w-4" />
                        )}
                    </Button>
                )}
                {/* 44 px au doigt, 36 au pointeur : c'est la SEULE sortie du tiroir
                    d'aide au téléphone (pas de clavier pour Échap, et le tiroir couvre
                    tout l'écran, donc aucun voile à taper). Mesurée à 36×36 par le
                    détecteur de modales le 2026-08-30 — sous le minimum tactile
                    d'Apple. L'icône, elle, ne change pas de taille. */}
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={onClose}
                    className="h-11 w-11 text-muted-foreground md:h-9 md:w-9"
                    aria-label={t('common.close')}
                >
                    <X className="h-5 w-5" />
                </Button>
            </div>
        </div>
    )
}
